import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { HarmReportProps, HarmReportsKebabOption } from './harmReports.type';
import { HarmReportsTableHeader } from './harmReportsTableHeader';
import { HarmReportsTableFilter } from './harmReportsTableFilter';
import Table from '../../component/table/table';
import {
  formatSelectedReportRefs,
  getHarmReportsKebabOptions,
  getHarmReportsTableHeader,
  getHarmReportTitle,
} from './harmReportesHelper';
import HarmReportForm from '../../component/harmReportForm/harmReportForm';
import { useHarmReports } from '../../hooks/useHarmReports';
import { HarmReportDetails } from './harmReportDetails';
import { getConsecutiveSweeps } from '../../utils/helper';
import { Trans } from 'react-i18next';
import UserVerificationModal from '../../component/userVerificationModal/userVerificationModal';
import { ConfirmationModalV2 } from '../../component/confirmationModalV2/confirmationModalV2';
import SelectPlayerModal from '../../component/selectPlayerModal/selectPlayerModal';
import { InterventionReportDetails } from '../interventionReports/interventionReportDetails';
import { HarmReport } from '@inplay/models';
import { InterventionReportModel } from '../../models/interventionReportModel';
import { reportService } from '../../service/instances';
import ModalComponent from '../../component/modal/modal';
import Loading from '../../assets/loading/loading';
import {
  getHarmReportEditPermissions,
  INTERVENTION_DURATION_MS,
  MANAGERS_REVIEW_DURATION_MS,
} from '../../utils/harmReportEditPermissions';
import { useUserDetailContext } from '../../service/context/userDetailContext';

export const HarmReports: FC<HarmReportProps> = () => {
  const {
    handleApplyFilters,
    handleClearFilters,
    handleOnChangeSearch,
    handleSelectionChange,
    filters,
    activeFiltersExist,
    handleReportHarmClose,
    handleKebabActionFromKebabMenu,
    harmReports,
    isTablet,
    loading,
    openCreateHarm,
    isReviewAndEditMode,
    openFilterModal,
    toggleFilterModal,
    selectedVenueBranch,
    handleDateRangeChange,
    selectedRows,
    setSelectedRows,
    selectedHarmReport,
    handleRowClick,
    closeHarmReportDetails,
    handleReviewAndSignClick,
    organizationContextLoading,
    harmReportForCreate,
    fetchHarmReports,
    t,
    handleDeleteHarmReports,
    handleDeleteClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleting,
    handleOnVerify,
    setShowUserVerification,
    showUserVerification,
    handleReportHarmClick,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    handleOnProceed,
    handleSettingMenuSelect,
    verifiedUser,
    playerData,
  } = useHarmReports();

  const { userDetail } = useUserDetailContext();
  const tooltipParentRef = useRef(null);

  const editDurationOptions = {
    interventionDurationMs: selectedVenueBranch?.interventionDurationMinutes
      ? selectedVenueBranch.interventionDurationMinutes * 60 * 1000
      : INTERVENTION_DURATION_MS,
    managersReviewDurationMs: selectedVenueBranch?.managersReviewDurationMinutes
      ? selectedVenueBranch.managersReviewDurationMinutes * 60 * 1000
      : MANAGERS_REVIEW_DURATION_MS,
  };

  const [viewingIntervention, setViewingIntervention] =
    useState<InterventionReportModel | null>(null);
  const [loadingIntervention, setLoadingIntervention] =
    useState<boolean>(false);

  const handleViewLinkedIntervention = useCallback(
    async (interventionId: string) => {
      setLoadingIntervention(true);
      if (!selectedVenueBranch) return;
      try {
        const reports: InterventionReportModel[] =
          await reportService.getInterventionReports({
            venueBranchId: selectedVenueBranch._id,
            currentDate: new Date(),
          });
        const found: InterventionReportModel | undefined = reports.find(
          (report: InterventionReportModel) =>
            report.originalReportId === interventionId,
        );
        if (found) setViewingIntervention(found);
      } catch (error) {
        console.error(
          '[HarmReports] Error fetching intervention report',
          error,
        );
      } finally {
        setLoadingIntervention(false);
      }
    },
    [selectedVenueBranch],
  );

  const tableHeader = useMemo(
    () =>
      getHarmReportsTableHeader(
        isTablet,
        t,
        tooltipParentRef,
        handleViewLinkedIntervention,
      ),
    [isTablet, handleViewLinkedIntervention],
  );

  return (
    <div className="header-content">
      <div
        className="table-content pt-6 flex flex-col overflow-hidden"
        style={{ boxShadow: '0px 0px 25px #2F41A51F' }}
      >
        {showUserVerification && (
          <UserVerificationModal
            show={showUserVerification}
            onToggle={() =>
              setShowUserVerification((currentValue: boolean) => !currentValue)
            }
            onVerified={handleOnVerify}
            scope={'hrmc'}
          />
        )}
        {showSelectPlayerModal && (
          <SelectPlayerModal
            onClose={() => {
              setShowSelectPlayerModal(false);
            }}
            onProceed={handleOnProceed}
          />
        )}
        <HarmReportsTableHeader
          searchQuery={filters.searchQuery}
          handleOnChangeSearch={handleOnChangeSearch}
          selectedCount={selectedRows.length}
          reportLabel={
            selectedRows.length > 1
              ? t('common.multi_report')
              : t('sweeps.report_one')
          }
          selectedDateRange={filters.selectedDateRange}
          disableSettingMenu={false}
          onSettingMenuSelect={handleSettingMenuSelect}
          handleReportHarmClick={handleReportHarmClick}
          handleDateRangeChange={handleDateRangeChange}
          handleClose={() => setSelectedRows([])}
          handleDelete={handleDeleteClick}
          handleDownload={() => {
            // TODO: Implement download functionality
            console.log('Download selected reports:', selectedRows);
          }}
          showDownloadLoading={false}
        >
          <HarmReportsTableFilter
            isOpenUserModal={openFilterModal}
            hasActiveFilters={activeFiltersExist}
            selectedBranchIDs={
              selectedVenueBranch ? [selectedVenueBranch._id] : []
            }
            handleClearFilters={handleClearFilters}
            handleApplyFilters={handleApplyFilters}
            toggleFilterModal={toggleFilterModal}
            currentDateRange={filters.selectedDateRange}
            currentFilters={filters}
            reports={harmReports}
          />
        </HarmReportsTableHeader>
        {/* Table    */}
        <div className="flex-grow">
          <Table<HarmReport>
            onSelectionChange={handleSelectionChange}
            tableHeader={tableHeader}
            selectedRows={selectedRows}
            tableData={harmReports}
            loading={loading || organizationContextLoading}
            onViewDetail={handleRowClick}
            onKebabSelect={(
              action: string,
              row: HarmReport,
              rowIndex: number,
            ) => {
              handleKebabActionFromKebabMenu(
                action as HarmReportsKebabOption,
                row,
                rowIndex,
              );
            }}
            loadingMenu={{ rowIndex: -1, action: '' }}
            getKebabMenuOptions={(
              row: any,
              loadingMenu: any,
              rowIndex: number,
            ) => {
              const perms = getHarmReportEditPermissions(
                row,
                userDetail?.type,
                userDetail?.id,
                editDurationOptions,
              );
              return getHarmReportsKebabOptions(
                row,
                loadingMenu,
                rowIndex,
                perms.canEdit,
                userDetail?.type,
                userDetail?.id,
              );
            }}
          />
        </div>
        {openCreateHarm && (
          <HarmReportForm
            title={getHarmReportTitle(
              harmReportForCreate?.reportRefNo,
              isReviewAndEditMode,
              t,
            )}
            data={harmReportForCreate}
            playerData={playerData}
            consecutiveSweeps={getConsecutiveSweeps(harmReportForCreate)}
            harmCount={null}
            interventionOffered={null}
            interventionRequested={null}
            isReviewAndEditMode={isReviewAndEditMode}
            onBack={handleReportHarmClose}
            verifiedUser={verifiedUser}
            onSuccess={fetchHarmReports}
            editPermissions={
              harmReportForCreate
                ? getHarmReportEditPermissions(
                    harmReportForCreate,
                    userDetail?.type,
                    userDetail?.id,
                    editDurationOptions,
                  )
                : undefined
            }
          />
        )}
        {selectedHarmReport && (
          <HarmReportDetails
            handleReviewAndSignClick={handleReviewAndSignClick}
            harmReport={selectedHarmReport}
            closeHarmReportDetails={closeHarmReportDetails}
            onKebabAction={(action: HarmReportsKebabOption) => {
              closeHarmReportDetails();
              handleKebabActionFromKebabMenu(action, selectedHarmReport, 0);
            }}
          />
        )}
        {loadingIntervention && !viewingIntervention && (
          <ModalComponent
            onToggle={() => setLoadingIntervention(false)}
            open={true}
            title=""
            isShowClose
            footerHidden
            dialogClassName="w-full max-w-[900px] h-[90vh] overflow-hidden"
          >
            <div className="flex justify-center items-center h-[calc(90vh-100px)]">
              <Loading />
            </div>
          </ModalComponent>
        )}
        {viewingIntervention && (
          <InterventionReportDetails
            interventionReport={viewingIntervention}
            onClose={() => setViewingIntervention(null)}
            onReviewAndSign={() => setViewingIntervention(null)}
          />
        )}
        {showDeleteConfirmation && (
          <ConfirmationModalV2
            note="harmReports.deletionNote"
            title="common.deleteReport"
            isOpen={showDeleteConfirmation}
            showConfirmButton
            onClose={() => setShowDeleteConfirmation(false)}
            message={
              <Trans
                i18nKey="harmReports.deleteConfirmation"
                values={{
                  value: formatSelectedReportRefs(selectedRows),
                  report:
                    selectedRows.length === 1
                      ? t('harmReports.singleReport')
                      : t('harmReports.multipleReports'),
                }}
                components={{ bold: <strong /> }}
              />
            }
            isLoading={deleting}
            onConfirm={handleDeleteHarmReports}
          />
        )}
      </div>
    </div>
  );
};
